/**
 * Profile Page
 *
 * Shows the signed-in user the information the portal knows about them,
 * sourced from the ID-token claims (`/oauth-agent/claims`) and the OIDC
 * userinfo response (`/oauth-agent/userinfo`).
 *
 * When debug mode is enabled (the runtime `debug` flag — see src/config.ts),
 * the page additionally renders an authorization-diagnostics panel plus the raw
 * identity (`/me`), claims, and userinfo payloads as pretty-printed JSON — to
 * aid troubleshooting of SSO / claim-mapping issues and the invite-affordance
 * gate (`canManageMembers`).
 *
 * Protected by AuthGuard at the route level, so it is reachable by any
 * authenticated user — admin or not.
 *
 * @module pages/ProfilePage
 */

import {
  Box,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { TopBar } from '../components/TopBar';
import { useAuthStore } from '../stores/useAuthStore';
import { isDebugEnabled } from '../config';
import type { Identity } from '../types/identity';

// =============================================================================
// Capability State
// =============================================================================

/**
 * The four states of a boolean capability field on the `/me` identity, kept
 * distinct so `absent` (backend didn't send it) never collapses into `false`
 * (backend evaluated it as no) — the whole point of the diagnostics panel.
 */
type CapabilityState = 'true' | 'false' | 'absent' | 'unknown';

const CAPABILITY_CHIP: Record<
  CapabilityState,
  { label: string; color: 'success' | 'error' | 'warning' | 'default' }
> = {
  true: { label: 'true', color: 'success' },
  false: { label: 'false', color: 'error' },
  absent: { label: 'absent (field not returned)', color: 'warning' },
  unknown: { label: 'unknown (identity not loaded)', color: 'default' },
};

/** CapabilityChip - a coloured chip for a {@link CapabilityState}. */
function CapabilityChip({ state }: { state: CapabilityState }) {
  const { label, color } = CAPABILITY_CHIP[state];
  return (
    <Chip
      size="small"
      label={label}
      color={color}
      data-testid={`capability-chip-${state}`}
    />
  );
}

// =============================================================================
// Field Row
// =============================================================================

interface ProfileFieldProps {
  /** Human-readable field name. */
  label: string;
  /** Field value; an empty value renders a muted "Not provided" placeholder. */
  value?: React.ReactNode;
  /** `data-testid` for the row. */
  testId: string;
}

/**
 * ProfileField - A single label/value row in the profile details list.
 *
 * Empty string values (and `undefined`/`null`) render a muted "Not provided"
 * placeholder so the row is still present and the layout stays predictable.
 */
function ProfileField({ label, value, testId }: ProfileFieldProps) {
  const isEmpty = value === undefined || value === null || value === '';

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{ py: 1.5 }}
      data-testid={testId}
    >
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          width: { sm: 200 },
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>

      {isEmpty ? (
        <Typography
          variant="body2"
          data-testid={`${testId}-empty`}
          sx={{
            color: 'text.disabled',
            fontStyle: 'italic',
          }}
        >
          Not provided
        </Typography>
      ) : typeof value === 'string' ? (
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {value}
        </Typography>
      ) : (
        value
      )}
    </Stack>
  );
}

// =============================================================================
// Debug Panel
// =============================================================================

interface DebugPanelProps {
  /** Panel heading. */
  title: string;
  /** Payload rendered as pretty-printed JSON. */
  payload: unknown;
  /** `data-testid` for the panel. */
  testId: string;
}

/**
 * DebugPanel - Renders a raw payload as pretty-printed JSON.
 *
 * Only mounted when debug mode is enabled; surfaces the exact claims /
 * userinfo the SPA received so SSO and claim-mapping issues can be diagnosed
 * without browser devtools.
 */
function DebugPanel({ title, payload, testId }: DebugPanelProps) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={testId}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Box
        component="pre"
        data-testid={`${testId}-json`}
        sx={{
          m: 0,
          p: 2,
          bgcolor: 'grey.100',
          borderRadius: 1,
          overflowX: 'auto',
          fontSize: '0.8rem',
          fontFamily: 'monospace',
        }}
      >
        {payload === null || payload === undefined
          ? 'null'
          : JSON.stringify(payload, null, 2)}
      </Box>
    </Paper>
  );
}

// =============================================================================
// Authorization Diagnostics
// =============================================================================

/**
 * AuthorizationDiagnostics - A readable interpretation of the authorization
 * fields on the `/api/tenant-management/me` identity, for troubleshooting the
 * invite affordance ("You don't have permission to manage members…").
 *
 * The invite gate is `identity.canManageMembers` (home-org authority ∧
 * platform-project entitlement, resolved live from ZITADEL). When it reads
 * `false`, this panel disambiguates the two failure shapes that matter:
 *
 *   - **`false`** — the backend evaluated the capability and said no (either no
 *     ZITADEL `user.write` authority in this org, or the org isn't entitled to
 *     the platform project).
 *   - **absent** — the backend didn't return the field at all, i.e. it predates
 *     the capability API. The SPA then defaults it to `false`, so the button is
 *     disabled even for a legitimately-capable admin. This is a deploy-ordering
 *     symptom (frontend ahead of `tenant-management-tools`), not an authz denial.
 *
 * We must therefore report absence distinctly from `false`. `Identity` types
 * `canManageMembers` as a required boolean, but at runtime an older backend can
 * omit it — so we inspect the raw object for the key's presence.
 */
function AuthorizationDiagnostics({ identity }: { identity: Identity | null }) {
  const hasField =
    identity !== null &&
    Object.prototype.hasOwnProperty.call(identity, 'canManageMembers');

  const canManageMembers: CapabilityState = !identity
    ? 'unknown'
    : !hasField
      ? 'absent'
      : identity.canManageMembers
        ? 'true'
        : 'false';

  const rows: Array<{ label: string; value: React.ReactNode; testId: string }> =
    [
      {
        label: 'canManageMembers (invite gate)',
        value: <CapabilityChip state={canManageMembers} />,
        testId: 'profile-authz-can-manage',
      },
      {
        label: 'isUserAdmin (platform.user.admin)',
        value: String(identity?.isUserAdmin ?? '—'),
        testId: 'profile-authz-user-admin',
      },
      {
        label: 'isStaffAdmin (platform.staff.admin)',
        value: String(identity?.isStaffAdmin ?? '—'),
        testId: 'profile-authz-staff-admin',
      },
      {
        label: 'Home org',
        value: identity
          ? `${identity.activeOrg.name || '(no name)'} — ${identity.activeOrg.id || '(no id)'}`
          : '—',
        testId: 'profile-authz-home-org',
      },
      {
        label: 'roles',
        value: identity ? identity.roles.join(', ') || '(none)' : '—',
        testId: 'profile-authz-roles',
      },
    ];

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      data-testid="profile-authz-diagnostics"
    >
      <Typography variant="subtitle2" gutterBottom>
        Authorization (invite gate)
      </Typography>
      <Stack divider={<Divider />} spacing={0}>
        {rows.map((row) => (
          <Stack
            key={row.testId}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 0.25, sm: 2 }}
            sx={{ py: 1 }}
            data-testid={row.testId}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                width: { sm: 260 },
                flexShrink: 0,
                fontFamily: 'monospace',
              }}
            >
              {row.label}
            </Typography>
            {typeof row.value === 'string' ? (
              <Typography
                variant="body2"
                sx={{ wordBreak: 'break-word', fontFamily: 'monospace' }}
              >
                {row.value}
              </Typography>
            ) : (
              row.value
            )}
          </Stack>
        ))}
      </Stack>
      {canManageMembers === 'absent' && (
        <Typography
          variant="body2"
          data-testid="profile-authz-absent-hint"
          sx={{
            color: 'warning.main',
            mt: 1.5,
          }}
        >
          The backend did not return <code>canManageMembers</code>. It likely
          predates the capability API, so the SPA defaults it to{' '}
          <code>false</code> and disables the invite button. Deploy the
          tenant-management-tools change.
        </Typography>
      )}
      {canManageMembers === 'false' && (
        <Typography
          variant="body2"
          data-testid="profile-authz-false-hint"
          sx={{
            color: 'text.secondary',
            mt: 1.5,
          }}
        >
          The backend evaluated the capability and returned <code>false</code>:
          either no ZITADEL <code>user.write</code> authority in this org, or
          the org isn&apos;t entitled to the platform project. Check the{' '}
          <code>tenant-management-tools</code> logs for the capability probe.
        </Typography>
      )}
    </Paper>
  );
}

// =============================================================================
// ProfilePage Component
// =============================================================================

/**
 * ProfilePage - The signed-in user's own profile.
 *
 * Features:
 * - TopBar navigation at the top
 * - A details card listing the information known about the user, drawn from
 *   the ID-token claims and the OIDC userinfo response
 * - Debug panels (raw claims + userinfo JSON) when debug mode is enabled
 *
 * Protected by AuthGuard at the route level.
 */
export function ProfilePage() {
  const claims = useAuthStore((state) => state.claims);
  const userinfo = useAuthStore((state) => state.userinfo);
  const identity = useAuthStore((state) => state.identity);
  const isUserAdmin = useAuthStore((state) => state.isUserAdmin);
  const orgName = useAuthStore((state) => state.orgName);
  const userDisplayName = useAuthStore((state) => state.userDisplayName);

  const debug = isDebugEnabled();

  /**
   * Resolve a string field, preferring the ID-token claims and falling back
   * to userinfo when the claim is absent. Blank/whitespace values are treated
   * as missing.
   */
  const pick = (key: string): string | undefined => {
    const fromClaims = claims?.[key];
    if (typeof fromClaims === 'string' && fromClaims.trim()) {
      return fromClaims;
    }
    const fromUserinfo = userinfo?.[key];
    if (typeof fromUserinfo === 'string' && fromUserinfo.trim()) {
      return fromUserinfo;
    }
    return undefined;
  };

  const emailVerified = claims?.email_verified ?? userinfo?.email_verified;

  // Email verification status renders as a chip, or "Not provided" when the
  // claim is absent entirely.
  const emailVerifiedValue =
    emailVerified === undefined ? undefined : (
      <Chip
        size="small"
        label={emailVerified ? 'Verified' : 'Not verified'}
        color={emailVerified ? 'success' : 'default'}
        data-testid="profile-email-verified-chip"
      />
    );

  const accessValue = (
    <Chip
      size="small"
      label={isUserAdmin ? 'Administrator' : 'Standard user'}
      color={isUserAdmin ? 'primary' : 'default'}
      data-testid="profile-access-chip"
    />
  );

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
      data-testid="profile-page"
    >
      {/* Top navigation bar */}
      <TopBar />

      {/* Main content area */}
      <Container
        maxWidth="md"
        sx={{ flexGrow: 1, py: 4 }}
        data-testid="profile-page-content"
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ mb: 1 }}
          data-testid="profile-page-title"
        >
          Your profile
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 3,
          }}
        >
          The information the portal holds about your account.
        </Typography>

        {/* Details card */}
        <Paper
          variant="outlined"
          sx={{ px: 3, py: 1 }}
          data-testid="profile-details"
        >
          <ProfileField
            label="Name"
            value={userDisplayName}
            testId="profile-field-name"
          />
          <Divider />
          <ProfileField
            label="Email"
            value={pick('email')}
            testId="profile-field-email"
          />
          <Divider />
          <ProfileField
            label="Email status"
            value={emailVerifiedValue}
            testId="profile-field-email-verified"
          />
          <Divider />
          <ProfileField
            label="Username"
            value={pick('preferred_username')}
            testId="profile-field-username"
          />
          <Divider />
          <ProfileField
            label="First name"
            value={pick('given_name')}
            testId="profile-field-given-name"
          />
          <Divider />
          <ProfileField
            label="Last name"
            value={pick('family_name')}
            testId="profile-field-family-name"
          />
          <Divider />
          <ProfileField
            label="Nickname"
            value={pick('nickname')}
            testId="profile-field-nickname"
          />
          <Divider />
          <ProfileField
            label="Locale"
            value={pick('locale')}
            testId="profile-field-locale"
          />
          <Divider />
          <ProfileField
            label="Organization"
            value={orgName}
            testId="profile-field-org-name"
          />
          <Divider />
          <ProfileField
            label="Organization ID"
            value={pick('urn:zitadel:iam:org:id')}
            testId="profile-field-org-id"
          />
          <Divider />
          <ProfileField
            label="User ID"
            value={typeof claims?.sub === 'string' ? claims.sub : undefined}
            testId="profile-field-sub"
          />
          <Divider />
          <ProfileField
            label="Access level"
            value={accessValue}
            testId="profile-field-access"
          />
        </Paper>

        {/* Debug panels - only when debug mode is enabled */}
        {debug && (
          <Box sx={{ mt: 4 }} data-testid="profile-debug">
            <Typography
              variant="h6"
              component="h2"
              sx={{ mb: 1 }}
              data-testid="profile-debug-title"
            >
              Debug
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 2,
              }}
            >
              Authorization diagnostics and the raw payloads received from the
              auth backend (identity, claims, userinfo). Visible because the{' '}
              <code>debug</code> runtime flag is enabled.
            </Typography>
            <Stack spacing={2}>
              <AuthorizationDiagnostics identity={identity} />
              <DebugPanel
                title="Identity (/api/tenant-management/me)"
                payload={identity}
                testId="profile-debug-identity"
              />
              <DebugPanel
                title="ID token claims"
                payload={claims}
                testId="profile-debug-claims"
              />
              <DebugPanel
                title="Userinfo"
                payload={userinfo}
                testId="profile-debug-userinfo"
              />
            </Stack>
          </Box>
        )}
      </Container>
    </Box>
  );
}
